import React from 'react';
import { motion } from 'framer-motion';
interface HeroSectionProps {
  onOpenDemo: () => void;
}
export function HeroSection({
  onOpenDemo
}: HeroSectionProps) {
  return <section className="relative w-full pt-32 pb-20 md:pt-28 md:pb-0 bg-midnight-navy overflow-hidden" data-id="element-155">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center" data-id="element-156">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="space-y-8 relative z-10" data-id="element-157">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="text-stark-white text-4xl md:text-6xl font-manrope font-bold leading-[1.1] tracking-tight" data-id="element-158">
            Stop Guessing Where Your Restaurant Money Is Going.
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease: 'easeOut'
        }} className="text-stark-white/70 text-lg md:text-xl font-inter leading-relaxed max-w-xl" data-id="element-159">
            See your real food costs, labor costs, and spending — faster. ROI
            helps restaurant owners understand their money in, money out, and
            operational blind spots without waiting months for complicated
            setups.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6,
          ease: 'easeOut'
        }} className="flex flex-col sm:flex-row gap-4" data-id="element-160">
            <button onClick={onOpenDemo} className="bg-accent-red text-stark-white px-8 py-4 rounded-full font-bold hover:bg-red-700 transition-all shadow-xl shadow-accent-red/20 active:scale-95" data-id="element-161">
              Book a Demo
            </button>
            <a href="#how-it-works" className="border border-stark-white/30 text-stark-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-all text-center active:scale-95" data-id="element-162">
              See How It Works
            </a>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1,
        delay: 0.4,
        ease: 'easeOut'
      }} className="relative hidden md:block group overflow-hidden" data-id="element-163">
          {/* Sinusoidal Wave Background - Enhanced Visibility & Interactivity */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden select-none" data-id="element-164">
            <motion.svg viewBox="0 0 800 600" className="w-[130%] h-[130%] opacity-40" initial={{
            rotate: -15
          }} animate={{
            rotate: [-15, -10, -15]
          }} transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-165">
              <defs data-id="element-166">
                <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-167">
                  <stop offset="0%" stopColor="#E30B13" stopOpacity="0" data-id="element-168" />
                  <stop offset="50%" stopColor="#E30B13" stopOpacity="0.8" data-id="element-169" />
                  <stop offset="100%" stopColor="#E30B13" stopOpacity="0" data-id="element-170" />
                </linearGradient>
                <filter id="glow" data-id="element-171">
                  <feGaussianBlur stdDeviation="5" result="blur" data-id="element-172" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-173" />
                </filter>
              </defs>

              {/* Primary Glowing Wave */}
              <motion.path d="M 0 300 Q 100 100 200 300 T 400 300 T 600 300 T 800 300" fill="none" stroke="url(#waveGrad)" strokeWidth="60" strokeLinecap="round" filter="url(#glow)" animate={{
              d: ['M 0 300 Q 100 100 200 300 T 400 300 T 600 300 T 800 300', 'M 0 300 Q 100 500 200 300 T 400 300 T 600 300 T 800 300', 'M 0 300 Q 100 100 200 300 T 400 300 T 600 300 T 800 300'],
              x: [-100, 0],
              strokeWidth: [60, 80, 60]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-174" />

              {/* Secondary Accent Wave */}
              <motion.path d="M 0 350 Q 150 150 300 350 T 600 350 T 900 350" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="30" strokeLinecap="round" animate={{
              d: ['M 0 350 Q 150 150 300 350 T 600 350 T 900 350', 'M 0 350 Q 150 550 300 350 T 600 350 T 900 350', 'M 0 350 Q 150 150 300 350 T 600 350 T 900 350'],
              x: [0, -150]
            }} transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-175" />

              {/* Particles following the wave */}
              {[...Array(6)].map((_, i) => <motion.circle key={i} r="4" fill="#E30B13" animate={{
              cx: [0, 800],
              cy: [300, 350, 250, 300],
              opacity: [0, 1, 0]
            }} transition={{
              duration: 4,
              delay: i * 0.8,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-176" />)}
            </motion.svg>
          </div>

          <motion.svg whileHover={{
          scale: 1.05,
          rotate: 1
        }} animate={{
          y: [0, -15, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} viewBox="0 0 500 500" className="w-full h-auto opacity-95 relative z-10 cursor-pointer drop-shadow-[0_0_15px_rgba(227,11,19,0.3)]" data-id="element-177">
            <defs data-id="element-178">
              <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-179">
                <stop offset="0%" style={{
                stopColor: '#E30B13',
                stopOpacity: 0.8
              }} data-id="element-180" />
                <stop offset="100%" style={{
                stopColor: '#091D32',
                stopOpacity: 0
              }} data-id="element-181" />
              </linearGradient>
            </defs>
            {/* Abstract data lines */}
            <path d="M50,250 Q150,50 250,250 T450,250" fill="none" stroke="url(#grad1)" strokeWidth="2" strokeDasharray="5,5" data-id="element-182" />
            <path d="M50,300 Q200,100 350,300 T500,100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" data-id="element-183" />
            <circle cx="250" cy="250" r="100" fill="none" stroke="rgba(227, 11, 19, 0.2)" strokeWidth="1" data-id="element-184" />
            <circle cx="250" cy="250" r="150" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" data-id="element-185" />
            {/* Connecting dots */}
            <circle cx="150" cy="150" r="4" fill="#E30B13" data-id="element-186" />
            <circle cx="350" cy="350" r="4" fill="#E30B13" data-id="element-187" />
            <circle cx="250" cy="250" r="6" fill="#E30B13" data-id="element-188" />
            {/* Flow arrows */}
            <path d="M250,250 L280,280 M280,280 L270,280 M280,280 L280,270" stroke="#E30B13" strokeWidth="2" data-id="element-189" />
          </motion.svg>
        </motion.div>
      </div>
    </section>;
}